import React from "react";
import { useNavigate } from "react-router-dom";
import Moment from "react-moment";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, ZoomControl } from "react-leaflet";
import { useJsApiLoader } from "@react-google-maps/api";
import { Layers } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "react-toastify";
import { getData } from "../../../../Services/Services";
import { sopInstanceList, getThemeURL, getIncidentSummary, getBlockAndDistrictBoundaryURL } from "../../../../Services/EndPoints";
import "./ViewIncident.css";

const parseTime = (str) => (str ? new Date(str.replace(" ", "T")) : null);

// Same key + libraries list used everywhere else in the app (ActivePanelView,
// DashboardMap, RMMap) — @react-google-maps/api's loader is a page-wide
// singleton that throws if called again with a different "libraries" array,
// so this must match exactly rather than just requesting what this file needs.
const GOOGLE_MAPS_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";
const GOOGLE_PLACES_LIBRARIES = ["drawing", "geometry", "places", "visualization"];

// Real-world categories Google Places actually indexes (unlike the DMS's own
// custom facility types), searched within this radius of the incident.
const NEARBY_PLACE_TYPES = [
  { type: "hospital", color: "#2563EB", emoji: "🏥", label: "Hospital" },
  { type: "fire_station", color: "#DC2626", emoji: "🚒", label: "Fire Station" },
  { type: "police", color: "#3B82F6", emoji: "👮", label: "Police Station" },
  { type: "pharmacy", color: "#10B981", emoji: "💊", label: "Pharmacy" },
  { type: "school", color: "#F59E0B", emoji: "🏫", label: "School (Shelter)" },
  { type: "local_government_office", color: "#8B5CF6", emoji: "🏛️", label: "Govt. Office" },
  { type: "lodging", color: "#7C3AED", emoji: "🏨", label: "Hotel (Refuge)" },
  { type: "gas_station", color: "#059669", emoji: "⛽", label: "Fuel Station" },
];
const NEARBY_SEARCH_RADIUS_M = 10000;

// Same simple teardrop marker used by the Create Alert location preview map,
// optionally carrying a category emoji (kept upright — only the teardrop
// itself is rotated to a point).
function makePinIcon(color, emoji) {
  return L.divIcon({
    className: "",
    html: `
      <div style="
        width: 28px; height: 28px; border-radius: 50% 50% 50% 0;
        background: ${color};
        border: 3px solid #fff;
        transform: rotate(-45deg);
        box-shadow: 0 3px 8px rgba(0,0,0,0.35);
        display: flex; align-items: center; justify-content: center;
      ">${emoji ? `<span style="transform: rotate(45deg); font-size: 13px; line-height: 1;">${emoji}</span>` : ""}</div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

// Small round marker for a nearby Places result — deliberately less
// prominent than the incident's own teardrop pin.
function makeFacilityIcon(color, emoji) {
  return L.divIcon({
    className: "",
    html: `
      <div style="
        width: 22px; height: 22px; border-radius: 50%;
        background: #fff; border: 2px solid ${color};
        box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        display: flex; align-items: center; justify-content: center;
        font-size: 11px; line-height: 1;
      ">${emoji || ""}</div>
    `,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

// Same category → color/emoji mapping used on the Active Panel map legend,
// so an incident's pin reads consistently with the rest of the app.
const DISASTER_ICON_CONFIGS = {
  FLOOD: { color: "#3B82F6", icon: "💧" },
  Flood: { color: "#3B82F6", icon: "💧" },
  COLD_WAVE: { color: "#6B7280", icon: "❄️" },
  "Cold Wave": { color: "#6B7280", icon: "❄️" },
  EARTHQUAKE: { color: "#8B5CF6", icon: "🌍" },
  Earthquake: { color: "#8B5CF6", icon: "🌍" },
  FIRE: { color: "#DC2626", icon: "🔥" },
  Fire: { color: "#DC2626", icon: "🔥" },
  CYCLONE: { color: "#F59E0B", icon: "🌀" },
  Cyclone: { color: "#F59E0B", icon: "🌀" },
  "Heat Wave": { color: "#F97316", icon: "🔥" },
  HEAT_WAVE: { color: "#F97316", icon: "🔥" },
  "Cloud Burst": { color: "#0EA5E9", icon: "⛈️" },
  CLOUD_BURST: { color: "#0EA5E9", icon: "⛈️" },
  GLOF: { color: "#0891B2", icon: "🌊" },
  Drought: { color: "#D97706", icon: "🌵" },
  DROUGHT: { color: "#D97706", icon: "🌵" },
  Landslide: { color: "#92400E", icon: "⛰️" },
  LANDSLIDE: { color: "#92400E", icon: "⛰️" },
  Tsunami: { color: "#0891B2", icon: "🌊" },
  TSUNAMI: { color: "#0891B2", icon: "🌊" },
  Tornado: { color: "#7C3AED", icon: "🌪️" },
  TORNADO: { color: "#7C3AED", icon: "🌪️" },
  Avalanche: { color: "#64748B", icon: "🏔️" },
  AVALANCHE: { color: "#64748B", icon: "🏔️" },
  Wildfire: { color: "#DC2626", icon: "🔥" },
  WILDFIRE: { color: "#DC2626", icon: "🔥" },
  DEFAULT: { color: "#DC3545", icon: "🚨" },
};

const getDisasterMeta = (category) => DISASTER_ICON_CONFIGS[category] || DISASTER_ICON_CONFIGS.DEFAULT;

// Same base-layer set used elsewhere in the app (Create Alert, Disaster
// Location map) so this map's Layers control behaves identically.
const BASE_LAYERS = {
  road: { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenStreetMap contributors", subdomains: ["a", "b", "c"] },
  satellite: { url: "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", attribution: "", subdomains: ["mt0", "mt1", "mt2", "mt3"] },
  terrain: { url: "https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}", attribution: "", subdomains: ["mt0", "mt1", "mt2", "mt3"] },
  hybrid: { url: "https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", attribution: "", subdomains: ["mt0", "mt1", "mt2", "mt3"] },
};
const LAYER_OPTIONS = [
  { id: "road", name: "Road View" },
  { id: "satellite", name: "Satellite View" },
  { id: "terrain", name: "Terrain View" },
  { id: "hybrid", name: "Hybrid View" },
];

// ── Map Layers toggle — identical control to the one on the Disaster
// Location map (Acknowledge Disaster screen), just scoped to this file. ──
const IncidentMapLayersControl = ({ activeLayer, onLayerChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <div style={{ position: "absolute", top: "10px", right: "10px", zIndex: 1000 }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        title="Map Layers"
        style={{
          width: "40px", height: "40px", border: "none", borderRadius: "8px",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)", transition: "all 0.2s ease",
          backgroundColor: isOpen ? "#3B82F6" : "white",
        }}
      >
        <Layers size={20} color={isOpen ? "white" : "#374151"} strokeWidth={2.5} />
      </button>
      {isOpen && (
        <div style={{
          position: "absolute", top: "50px", right: 0, backgroundColor: "white",
          borderRadius: "10px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          minWidth: "180px", overflow: "hidden",
        }}>
          <div style={{ padding: "10px", borderBottom: "1px solid #E5E7EB", backgroundColor: "#F0F6FB", fontSize: "13px", fontWeight: 700, color: "#0F2D42" }}>
            Map Layers
          </div>
          <div style={{ padding: "6px" }}>
            {LAYER_OPTIONS.map((layer) => (
              <div
                key={layer.id}
                onClick={() => { onLayerChange(layer.id); setIsOpen(false); }}
                style={{
                  padding: "10px", cursor: "pointer", borderRadius: "6px", marginBottom: "4px",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  backgroundColor: activeLayer === layer.id ? "#EFF6FF" : "transparent",
                  borderLeft: activeLayer === layer.id ? "3px solid #3B82F6" : "3px solid transparent",
                }}
              >
                <span style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>{layer.name}</span>
                {activeLayer === layer.id && <span style={{ color: "#3B82F6", fontSize: "14px" }}>✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Fullscreen toggle — identical control to the Disaster Location map's. ──
const IncidentMapFullscreenControl = ({ targetRef }) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    const el = targetRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <button
      onClick={toggleFullscreen}
      title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
      style={{
        position: "absolute", top: "58px", right: "10px", zIndex: 1000,
        width: 36, height: 36, borderRadius: 8,
        border: isFullscreen ? "2px solid #3b82f6" : "1px solid rgba(0,0,0,0.15)",
        background: isFullscreen ? "linear-gradient(135deg,#eff6ff,#dbeafe)" : "#ffffff",
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: isFullscreen ? "0 2px 12px rgba(59,130,246,0.35)" : "0 2px 6px rgba(0,0,0,0.15)",
        transition: "all 0.2s ease", flexShrink: 0,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      {isFullscreen ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
        </svg>
      )}
    </button>
  );
};

const getLatLng = (gisConfigStr) => {
  if (!gisConfigStr) return null;
  try {
    const gisConfig = JSON.parse(gisConfigStr);
    const center = gisConfig?.incidentcenter;
    if (Array.isArray(center) && center.length >= 2) {
      const [lng, lat] = center;
      if (!isNaN(lat) && !isNaN(lng)) return { lat: parseFloat(lat), lng: parseFloat(lng) };
    }
  } catch (e) {
    // ignore malformed gis_config
  }
  return null;
};

// The API returns block/district boundaries as a stringified GeoJSON
// geometry (e.g. {"type":"MultiPolygon","coordinates":[...]}) — parse it
// into a Feature so react-leaflet's <GeoJSON> can render it directly.
const parseBoundaryGeoJSON = (boundaryStr) => {
  if (!boundaryStr) return null;
  try {
    const geometry = JSON.parse(boundaryStr);
    if (!geometry?.type || !geometry?.coordinates) return null;
    return { type: "Feature", properties: {}, geometry };
  } catch (e) {
    return null;
  }
};

// Maps an activity remark to an icon + color, and splits it into a short
// title (text before the first colon) and a fuller description.
// Matches remarks like "Auto SOP execution failed" / "SOP auto-failed", however
// "auto"/"sop"/"fail" get ordered by the backend.
const AUTO_SOP_FAILED_TEST = /auto.*sop.*fail|sop.*auto.*fail/i;

const ACTIVITY_KEYWORDS = [
  { test: /incident created/i, icon: "🚨", color: "#EF4444", bg: "#FEE2E2" },
  { test: AUTO_SOP_FAILED_TEST, icon: "⚠️", color: "#EF4444", bg: "#FEE2E2" },
  { test: /sop cancelled/i, icon: "⚠️", color: "#EF4444", bg: "#FEE2E2" },
  { test: /sop completed/i, icon: "🏁", color: "#4ADE80", bg: "#DCFCE7" },
  { test: /sop closed/i, icon: "🔒", color: "#9CA3AF", bg: "#F3F4F6" },
  { test: /sop executed/i, icon: "📋", color: "#2E6DA4", bg: "#EBF4FA" },
  { test: /resources? deployed/i, icon: "🚚", color: "#FBBF24", bg: "#FEF3C7" },
  { test: /resources? released/i, icon: "🔓", color: "#38BDF8", bg: "#E0F2FE" },
  { test: /task.*(in ?progress)/i, icon: "🏃", color: "#A78BFA", bg: "#EDE9FE" },
  { test: /task completed/i, icon: "✅", color: "#4ADE80", bg: "#DCFCE7" },
  { test: /incident closed/i, icon: "🔚", color: "#9CA3AF", bg: "#F3F4F6" },
  { test: /alert generated/i, icon: "🔔", color: "#FBBF24", bg: "#FEF3C7" },
];

const activityMeta = (remarks) => {
  const found = ACTIVITY_KEYWORDS.find((k) => k.test.test(remarks || ""));
  return found || { icon: "📌", color: "#9CA3AF", bg: "#F3F4F6" };
};

const splitRemarks = (remarks) => {
  const text = remarks || "NA";
  const idx = text.indexOf(":");
  if (idx === -1) return { title: text, description: null };
  return { title: text.slice(0, idx).trim(), description: text.slice(idx + 1).trim() || null };
};

const severityMeta = (severity) => {
  const match = /level\s*(\d)/i.exec(severity || "");
  const level = match ? parseInt(match[1], 10) : null;
  if (level === 4) return { color: "#EF4444", bg: "#FEE2E2" };
  if (level === 3) return { color: "#FB923C", bg: "#FFF7ED" };
  if (level === 2) return { color: "#FBBF24", bg: "#FEF3C7" };
  if (level === 1) return { color: "#38BDF8", bg: "#E0F2FE" };
  return { color: "#9CA3AF", bg: "#F3F4F6" };
};

// Same keyword → color rules used for the "Current Stage"/status pills on the
// Incident List table, so a status reads with the same color here too.
const STAGE_COLOR_RULES = [
  { test: /cancel/i, bg: "#FEE2E2", color: "#EF4444" },
  { test: /fail/i, bg: "#FEF2F2", color: "#F87171" },
  { test: /clos/i, bg: "#DCFCE7", color: "#4ADE80" },
  { test: /complet/i, bg: "#DCFCE7", color: "#4ADE80" },
  { test: /progress/i, bg: "#FEF3C7", color: "#FBBF24" },
  { test: /deploy/i, bg: "#DBEAFE", color: "#60A5FA" },
  { test: /releas/i, bg: "#E0F2FE", color: "#38BDF8" },
  { test: /creat/i, bg: "#FFF7ED", color: "#FB923C" },
  { test: /sop/i, bg: "#EDE9FE", color: "#A78BFA" },
];
const getStageColors = (value) =>
  STAGE_COLOR_RULES.find((r) => r.test.test(value || "")) || { bg: "#F3F4F6", color: "#9CA3AF" };

// ── Section card wrapper ────────────────────────────────────────────────
const SectionCard = ({ icon, title, right, flush, onHeaderClick, children }) => (
  <div className="incv-card">
    <div
      className={`incv-card-header${onHeaderClick ? " incv-card-header--clickable" : ""}`}
      onClick={onHeaderClick}
    >
      <span className="incv-card-icon">{icon}</span>
      <span className="incv-card-title">{title}</span>
      {right && <span className="incv-card-right">{right}</span>}
    </div>
    {children && (
      <div className={`incv-card-body${flush ? " incv-card-body--flush" : ""}`}>{children}</div>
    )}
  </div>
);

const Field = ({ label, children }) => (
  <div className="incv-field">
    <div className="incv-field-label">{label}</div>
    <div className="incv-field-value">{children}</div>
  </div>
);

// ── Incident Location Map ─────────────────────────────────────────────
const IncidentMap = ({ latLng, incidentData }) => {
  const mapWrapperRef = React.useRef(null);
  const [activeLayer, setActiveLayer] = React.useState("road");
  const [isPopupOpen, setIsPopupOpen] = React.useState(false);
  const popupOpenHandlers = React.useMemo(
    () => ({ popupopen: () => setIsPopupOpen(true), popupclose: () => setIsPopupOpen(false) }),
    []
  );
  const categoryMeta = getDisasterMeta(incidentData?.category);
  const [boundary, setBoundary] = React.useState({
    blockName: null,
    districtName: null,
    blockBoundary: null,
    districtBoundary: null,
  });

  // region_ids can carry more than one id (comma-separated) — the boundary
  // lookup only takes a single region, so use the first one.
  const regionId = incidentData?.region_ids ? String(incidentData.region_ids).split(",")[0].trim() : null;

  React.useEffect(() => {
    if (!regionId) return;
    let cancelled = false;
    getData(`${getBlockAndDistrictBoundaryURL}/${regionId}`)
      .then((res) => {
        if (cancelled || !res) return;
        setBoundary({
          blockName: res.block_name || null,
          districtName: res.district_name || res.dist_name || null,
          blockBoundary: parseBoundaryGeoJSON(res.block_boundary),
          districtBoundary: parseBoundaryGeoJSON(res.district_boundary),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [regionId]);

  // Nearest real-world facilities (hospitals, fire stations, etc.) around the
  // incident point, sourced from Google Places — separate from the DMS's own
  // Facility Management data, which Places has no knowledge of.
  const { isLoaded: placesApiLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_PLACES_LIBRARIES,
  });
  const placesServiceRef = React.useRef(null);
  const [nearbyPlaces, setNearbyPlaces] = React.useState([]);
  const [loadingPlaces, setLoadingPlaces] = React.useState(false);
  const [hiddenPlaceTypes, setHiddenPlaceTypes] = React.useState({});

  React.useEffect(() => {
    if (!placesApiLoaded || !latLng || !window.google?.maps?.places) return;
    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement("div"));
    }
    const service = placesServiceRef.current;
    let cancelled = false;
    setLoadingPlaces(true);

    Promise.all(
      NEARBY_PLACE_TYPES.map(
        (cfg) =>
          new Promise((resolve) => {
            service.nearbySearch(
              { location: new window.google.maps.LatLng(latLng.lat, latLng.lng), radius: NEARBY_SEARCH_RADIUS_M, type: cfg.type },
              (results, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
                  resolve(
                    results.map((place) => ({
                      id: place.place_id,
                      name: place.name,
                      lat: place.geometry?.location?.lat(),
                      lng: place.geometry?.location?.lng(),
                      vicinity: place.vicinity || "",
                      layerType: cfg.type,
                    }))
                  );
                } else {
                  resolve([]);
                }
              }
            );
          })
      )
    ).then((groups) => {
      if (!cancelled) {
        setNearbyPlaces(groups.flat().filter((p) => !isNaN(p.lat) && !isNaN(p.lng)));
        setLoadingPlaces(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [placesApiLoaded, latLng]);

  const placesByType = React.useMemo(() => {
    const groups = {};
    nearbyPlaces.forEach((p) => {
      if (!groups[p.layerType]) groups[p.layerType] = [];
      groups[p.layerType].push(p);
    });
    return groups;
  }, [nearbyPlaces]);

  return (
    <SectionCard icon="📍" title="Incident Location" flush>
      <div className="incv-map-container" ref={mapWrapperRef} style={{ position: "relative" }}>
        {!latLng ? (
          <div className="incv-empty">Location not available for this incident.</div>
        ) : (
          <>
            <MapContainer
              center={latLng}
              zoom={12}
              minZoom={4}
              attributionControl={false}
              zoomControl={false}
              style={{ height: "100%", width: "100%" }}
            >
              {/* Default zoom control sits top-left, clashing with the Layers/Fullscreen
                  buttons on some incidents; bottom-right stays clear of every other overlay. */}
              <ZoomControl position="bottomright" />
              <TileLayer
                key={activeLayer}
                attribution={activeLayer === "road" ? "&copy; OpenStreetMap contributors" : ""}
                url={BASE_LAYERS[activeLayer].url}
                maxZoom={20}
                subdomains={BASE_LAYERS[activeLayer].subdomains}
              />
              {boundary.districtBoundary && (
                <GeoJSON
                  key={`district-${regionId}`}
                  data={boundary.districtBoundary}
                  style={{ color: "#6B7280", weight: 2, dashArray: "6 4", fillOpacity: 0.03 }}
                />
              )}
              {boundary.blockBoundary && (
                <GeoJSON
                  key={`block-${regionId}`}
                  data={boundary.blockBoundary}
                  style={{ color: "#DC2626", weight: 2, fillColor: "#DC2626", fillOpacity: 0.08 }}
                />
              )}
              <Marker position={latLng} icon={makePinIcon(categoryMeta.color, categoryMeta.icon)} eventHandlers={popupOpenHandlers}>
                <Popup>
                  <div className="incv-map-popup">
                    <div className="incv-map-popup-head">
                      <span className="incv-map-popup-icon">{categoryMeta.icon}</span>
                      <span className="incv-map-popup-title">{incidentData?.name || "NA"}</span>
                    </div>
                    <div className="incv-map-popup-grid">
                      <div className="incv-map-popup-field">
                        <div className="incv-map-popup-label">Incident Category</div>
                        <div className="incv-map-popup-value">{incidentData?.category || "NA"}</div>
                      </div>
                      <div className="incv-map-popup-field">
                        <div className="incv-map-popup-label">Incident Severity</div>
                        <div
                          className="incv-map-popup-chip"
                          style={{ color: severityMeta(incidentData?.severity).color, background: severityMeta(incidentData?.severity).bg }}
                        >
                          {incidentData?.severity || "NA"}
                        </div>
                      </div>
                      <div className="incv-map-popup-field">
                        <div className="incv-map-popup-label">Created By</div>
                        <div className="incv-map-popup-value">{incidentData?.created_by || "NA"}</div>
                      </div>
                      <div className="incv-map-popup-field">
                        <div className="incv-map-popup-label">Incident Date &amp; Time</div>
                        <div className="incv-map-popup-value">
                          {incidentData?.created_on ? (
                            <Moment format="DD-MM-YYYY, hh:mm A">{incidentData.created_on}</Moment>
                          ) : (
                            "NA"
                          )}
                        </div>
                      </div>
                      <div className="incv-map-popup-field">
                        <div className="incv-map-popup-label">Coordinates</div>
                        <div className="incv-map-popup-value">
                          {latLng ? `${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}` : "NA"}
                        </div>
                      </div>
                      <div className="incv-map-popup-field">
                        <div className="incv-map-popup-label">Current Status</div>
                        <div
                          className="incv-map-popup-chip"
                          style={{ color: getStageColors(incidentData?.current_status).color, background: getStageColors(incidentData?.current_status).bg }}
                        >
                          {incidentData?.current_status ? incidentData.current_status.replace(/_/g, " ") : "NA"}
                        </div>
                      </div>
                      <div className="incv-map-popup-field">
                        <div className="incv-map-popup-label">District</div>
                        <div className="incv-map-popup-value">{boundary.districtName || "NA"}</div>
                      </div>
                      <div className="incv-map-popup-field">
                        <div className="incv-map-popup-label">Block</div>
                        <div className="incv-map-popup-value">{boundary.blockName || "NA"}</div>
                      </div>
                      <div className="incv-map-popup-field">
                        <div className="incv-map-popup-label">Disaster Alert ID</div>
                        <div className="incv-map-popup-value">{incidentData?.disaster_notification_seq_id || incidentData?.sourceInfo?.disaster_notification_seq_id || "NA"}</div>
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
              {nearbyPlaces
                .filter((place) => !hiddenPlaceTypes[place.layerType])
                .map((place) => {
                  const cfg = NEARBY_PLACE_TYPES.find((c) => c.type === place.layerType);
                  return (
                    <Marker key={place.id} position={{ lat: place.lat, lng: place.lng }} icon={makeFacilityIcon(cfg?.color, cfg?.emoji)} eventHandlers={popupOpenHandlers}>
                      <Popup>
                        <div className="incv-map-popup" style={{ minWidth: "160px" }}>
                          <div className="incv-map-popup-head">
                            <span className="incv-map-popup-icon">{cfg?.emoji}</span>
                            <span className="incv-map-popup-title">{place.name}</span>
                          </div>
                          <div className="incv-map-popup-field">
                            <div className="incv-map-popup-label">{cfg?.label || "Facility"}</div>
                            <div className="incv-map-popup-value">{place.vicinity || "NA"}</div>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
            </MapContainer>
            <IncidentMapLayersControl activeLayer={activeLayer} onLayerChange={setActiveLayer} />
            <IncidentMapFullscreenControl targetRef={mapWrapperRef} />
            <NearbyFacilitiesLegend
              placesByType={placesByType}
              loading={loadingPlaces}
              hiddenTypes={hiddenPlaceTypes}
              onToggle={(type) => setHiddenPlaceTypes((prev) => ({ ...prev, [type]: !prev[type] }))}
              forceCollapsed={isPopupOpen}
            />
          </>
        )}
      </div>
    </SectionCard>
  );
};

// ── Nearby Facilities legend — real-world Places results near the incident,
// styled as a checkbox list with per-type counts (toggles marker visibility).
const NearbyFacilitiesLegend = ({ placesByType, loading, hiddenTypes, onToggle, forceCollapsed }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const effectiveExpanded = forceCollapsed ? false : isExpanded;
  const typesWithResults = NEARBY_PLACE_TYPES.filter((cfg) => placesByType[cfg.type]?.length > 0);

  if (!loading && typesWithResults.length === 0) return null;

  return (
    <div
      style={{
        position: "absolute", bottom: "10px", left: "10px", zIndex: 1000,
        background: "rgba(255,255,255,0.97)", borderRadius: "10px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)", minWidth: effectiveExpanded ? "200px" : "auto",
        maxHeight: "260px", overflow: "hidden", fontFamily: "'Segoe UI', sans-serif",
        opacity: forceCollapsed ? 0.6 : 1, pointerEvents: forceCollapsed ? "none" : "auto",
        transition: "opacity 0.2s ease",
      }}
    >
      <div
        onClick={() => !forceCollapsed && setIsExpanded(!isExpanded)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 12px", background: "#1a3f6f", color: "#fff", cursor: forceCollapsed ? "default" : "pointer",
          fontSize: "12px", fontWeight: 700, borderRadius: effectiveExpanded ? "10px 10px 0 0" : "10px",
        }}
      >
        <span>🧭 Nearby Facilities</span>
        <span style={{ fontSize: "11px" }}>{effectiveExpanded ? "▾" : "▸"}</span>
      </div>
      {effectiveExpanded && (
        <div style={{ padding: "6px", overflowY: "auto", maxHeight: "216px" }}>
          {loading ? (
            <div style={{ padding: "10px", fontSize: "12px", color: "#6B7280" }}>Searching nearby...</div>
          ) : (
            typesWithResults.map((cfg) => {
              const count = placesByType[cfg.type].length;
              const isVisible = !hiddenTypes[cfg.type];
              return (
                <div
                  key={cfg.type}
                  onClick={() => onToggle(cfg.type)}
                  style={{
                    display: "flex", alignItems: "center", gap: "8px", padding: "6px 8px",
                    borderRadius: "6px", cursor: "pointer", opacity: isVisible ? 1 : 0.4,
                  }}
                >
                  <input type="checkbox" checked={isVisible} onChange={() => onToggle(cfg.type)} onClick={(e) => e.stopPropagation()} style={{ accentColor: cfg.color, cursor: "pointer" }} />
                  <div style={{ width: "20px", height: "20px", borderRadius: "50%", border: `2px solid ${cfg.color}`, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", flexShrink: 0 }}>
                    {cfg.emoji}
                  </div>
                  <span style={{ fontSize: "11.5px", color: "#1a2a3a", fontWeight: 500 }}>{cfg.label} ({count})</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── Quick Links (condensed workflow milestones) ───────────────────────────
// Groups the raw status_id history into a handful of milestones, each with
// its constituent sub-stages shown as inline links — a fast-scan summary
// above the full Lifecycle stepper.
//
// Where each linked module lands when its Quick Link is clicked, keyed by the
// activity's reference_type. The matching id(s) (reference_id) are carried in
// navigation state so the destination list can bold the matched rows.
const NAV_BY_REF_TYPE = {
  SOP_INSTANCE: {
    path: "/Disaster-acknowledgereport/view-sop/Sop-instance-list",
    stateKey: "highlightSopIds",
  },
  RESOURCE_DEPLOY: {
    path: "/Disaster-acknowledgereport/resource-assign-list",
    stateKey: "highlightDeployIds",
  },
  INCIDENT: {
    path: "/Disaster-acknowledgereport/situation-awareness/incident-management",
    stateKey: "highlightIncidentId",
    single: true,
  },
};

// "Task In Progress / Completed" opens the SOP Instance detail view directly.
const SOP_INSTANCE_DETAIL_PATH = "/Disaster-acknowledgereport/view-sop/sops-instance";

// "Auto SOP Failed" links to the Execute SOP screen so the operator can run
// the SOP manually. Clicking it first pulls a small incident summary so the
// Execute SOP form can pre-select this incident.
const EXECUTE_SOP_PATH = "/Disaster-acknowledgereport/view-sop/execute-sops";

const QUICK_LINK_GROUPS = [
  {
    label: "SOP & Resources Activated",
    links: [
      { label: "SOP Executed", ids: [946] },
      { label: "Auto SOP Failed", ids: [956], action: "EXECUTE_SOP" },
      { label: "Resources Deployed", ids: [951] },
    ],
  },
  {
    label: "Task Execution",
    links: [
      { label: "Task In Progress", ids: [947] },
      { label: "Task Completed", ids: [948] },
    ],
  },
  {
    label: "SOP Outcome & Resources Released",
    links: [
      { label: "SOP Completed / Cancelled", ids: [949, 950] },
      { label: "Resources Released", ids: [952] },
    ],
  },
  {
    label: "Incident Closure",
    links: [
      { label: "SOP Closed", ids: [953] },
      { label: "Incident Closed", ids: [954] },
    ],
  },
];

const QuickLinks = ({ history, incidentData }) => {
  const navigate = useNavigate();

  if (!Array.isArray(history) || history.length === 0) return null;

  const milestones = QUICK_LINK_GROUPS.map((group) => {
    const linkStates = group.links.map((link) => {
      // Every history row for this link's status id(s) — not just the latest —
      // so a link that fired for several SOPs/resources carries all their ids.
      const entries = history
        .filter((h) => link.ids.includes(h.status_id))
        .map((h) => ({ ...h, t: parseTime(h.created_on) }))
        .sort((a, b) => b.t - a.t);
      const achievedEntry = entries[0];
      const refType = achievedEntry?.reference_type;
      const nav = refType ? NAV_BY_REF_TYPE[refType] : null;
      const refIds = [
        ...new Set(entries.map((e) => e.reference_id).filter((v) => v !== null && v !== undefined)),
      ];
      return { ...link, achieved: !!achievedEntry, entry: achievedEntry, nav, refType, refIds };
    });
    // If the SOP auto-failed for this incident, show only "Auto SOP Failed" in
    // this group — the sibling steps (SOP Executed / Resources Deployed) never
    // happened, so listing them as pending is misleading.
    const autoSopFailed = linkStates.some((l) => l.action === "EXECUTE_SOP" && l.achieved);
    const displayLinks = autoSopFailed
      ? linkStates.filter((l) => l.action === "EXECUTE_SOP" && l.achieved)
      : linkStates;
    const achievedEntries = displayLinks.filter((l) => l.achieved).map((l) => l.entry);
    const latest = achievedEntries.sort((a, b) => b.t - a.t)[0] || null;
    return { ...group, links: displayLinks, done: achievedEntries.length > 0, latest };
  });

  const handleLinkClick = async (link) => {
    if (!link.achieved) return;

    // "Auto SOP Failed" → fetch this incident's summary, then open Execute SOP
    // with it in navigation state so the form can pre-select the incident.
    if (link.action === "EXECUTE_SOP") {
      const incidentId = incidentData?.id;
      if (!incidentId) return;
      let summary = null;
      try {
        summary = await getData(`${getIncidentSummary}${incidentId}`);
      } catch (e) {
        // fall through and navigate with what we already have
      }
      navigate(EXECUTE_SOP_PATH, {
        state: {
          autoSopIncident: {
            ...(summary && typeof summary === "object" ? summary : {}),
            id: (summary && summary.id) || incidentId,
            name: (summary && summary.name) || incidentData?.name,
            severity: (summary && summary.severity) || incidentData?.severity,
            category: incidentData?.category,
          },
          sourceLabel: link.label,
          fromIncident: incidentData?.name || "",
        },
      });
      return;
    }

    if (link.refIds.length === 0) return;

    // Task instances live inside a SOP instance, and the timeline only gives us
    // the task's reference_id. Open the SOP Instance detail view directly for
    // this incident's SOP instance, carrying the task ids so its Task List can
    // expand + bold the matching task rows.
    if (link.refType === "TASK_INSTANCE") {
      const sopIds = [
        ...new Set(
          history
            .filter((h) => h.reference_type === "SOP_INSTANCE")
            .map((h) => h.reference_id)
            .filter((v) => v !== null && v !== undefined)
        ),
      ].map((v) => String(v));

      let sopInstance = null;
      try {
        const profile = await getData(getThemeURL);
        const stateId = profile?.[0]?.state_id;
        const allInstances = await getData(`${sopInstanceList}/${stateId}`);
        sopInstance = (allInstances || []).find((si) => sopIds.includes(String(si.id)));
      } catch (e) {
        // fall through to the id-only fallback below
      }

      if (!sopInstance && sopIds.length === 0) {
        toast.error("Could not resolve the SOP instance for this task.", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2500,
        });
        return;
      }

      navigate(SOP_INSTANCE_DETAIL_PATH, {
        state: {
          data: sopInstance || { id: sopIds[0] },
          highlightTaskIds: link.refIds,
          sourceLabel: link.label,
          fromIncident: incidentData?.name || "",
        },
      });
      return;
    }

    if (!link.nav) return;
    navigate(link.nav.path, {
      state: {
        [link.nav.stateKey]: link.nav.single
          ? (link.refIds[0] ?? incidentData?.id)
          : link.refIds,
        sourceLabel: link.label,
        fromIncident: incidentData?.name || "",
      },
    });
  };

  const doneCount = milestones.filter((m) => m.done).length;
  if (doneCount === 0) return null;

  return (
    <SectionCard
      icon="🔗"
      title="Quick Links"
      right={
        <span className="incv-pill" style={{ background: "#EDE9FE", color: "#7C3AED" }}>
          WORKFLOW STAGES
        </span>
      }
    >
      <div className="incv-quicklinks">
        {milestones.map((m, idx) => (
          <div className={`incv-quicklink-row${m.done ? " incv-quicklink-row--done" : ""}`} key={m.label}>
            <span className={`incv-quicklink-badge${m.done ? " incv-quicklink-badge--done" : ""}`}>
              {m.done ? "✓" : idx + 1}
            </span>
            <div className="incv-quicklink-body">
              <div className="incv-quicklink-title">
                {idx + 1}. {m.label}
              </div>
              <div className="incv-quicklink-links">
                {m.links.map((link, i) => {
                  const clickable =
                    link.achieved &&
                    (link.action === "EXECUTE_SOP" ||
                      (link.refIds.length > 0 && (link.nav || link.refType === "TASK_INSTANCE")));
                  return (
                    <React.Fragment key={link.label}>
                      {i > 0 && <span className="incv-quicklink-sep">|</span>}
                      <span
                        className={`incv-quicklink-link${link.achieved ? "" : " incv-quicklink-link--pending"}${
                          clickable ? " incv-quicklink-link--clickable" : ""
                        }`}
                        onClick={clickable ? () => handleLinkClick(link) : undefined}
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleLinkClick(link);
                                }
                              }
                            : undefined
                        }
                        role={clickable ? "button" : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        title={
                          !clickable
                            ? undefined
                            : link.action === "EXECUTE_SOP"
                            ? "Open Execute SOP for this incident"
                            : `View ${link.label} record(s)`
                        }
                      >
                        {link.label}
                      </span>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            {m.latest && (
              <div className="incv-quicklink-time">
                <Moment format="DD-MM-YYYY HH:mm">{m.latest.created_on}</Moment>
              </div>
            )}
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

// ── Incident Lifecycle (fixed stage stepper) ──────────────────────────────
// Always shows this canonical template, in this order. A stage lights up
// (with a timestamp) only if a matching entry is found in this incident's
// history; anything not reached yet stays dimmed ("Upcoming").
const isExceptionRemark = (remarks) => /cancel|fail/i.test(remarks || "");

// Some stages can resolve from more than one kind of history entry — e.g. the
// "SOP Executed" slot should also light up (as a failure) if the SOP never
// ran at all. `variants` lets a stage swap its label/icon based on which
// specific remark matched, while the exception (red) styling itself is still
// driven by `isExceptionRemark` below.
//
// "SOP Cancelled" gets its own dedicated slot right after "Resources
// Deployed" (rather than reusing the "SOP Completed" slot), so a cancelled
// SOP doesn't leave a confusing gap of dimmed nodes between the last
// completed stage and the exception — and "SOP Completed" then simply stays
// dimmed/upcoming for a cancelled SOP, since it genuinely never completed.
const LIFECYCLE_STAGES = [
  { label: "Alert Generated", icon: "🔔", test: /alert generated/i },
  { label: "Incident Created", icon: "🚨", test: /incident created/i },
  {
    label: "SOP Executed",
    icon: "📋",
    test: new RegExp(`sop executed|${AUTO_SOP_FAILED_TEST.source}`, "i"),
    variants: [{ test: AUTO_SOP_FAILED_TEST, label: "Auto SOP Failed", icon: "⚠️" }],
  },
  { label: "Resources Deployed", icon: "🚚", test: /resources? deployed/i },
  // Only shown when it actually happened — unlike the other stages, it
  // doesn't sit in the stepper as a permanent dimmed placeholder.
  { label: "SOP Cancelled", icon: "⚠️", test: /sop cancelled/i, optional: true },
  { label: "Task In Progress", icon: "🏃", test: /task.*(in ?progress)/i },
  { label: "Task Completed", icon: "✅", test: /task completed/i },
  { label: "SOP Completed", icon: "🏁", test: /sop completed/i },
  { label: "Resources Released", icon: "🔓", test: /resources? released/i },
  { label: "SOP Closed", icon: "🔒", test: /sop closed/i },
  { label: "Incident Closed", icon: "🔚", test: /incident closed/i },
];

// Picks the matched variant's label/icon for a stage (if any), falling back
// to the stage's default — e.g. shows "SOP Cancelled" instead of the
// default "SOP Completed" once the matched entry's remark says so.
const resolveStageDisplay = (stage, entry) => {
  if (!entry) return { label: stage.label, icon: stage.icon };
  const variant = (stage.variants || []).find((v) => v.test.test(entry.remarks || ""));
  return variant ? { label: variant.label, icon: variant.icon } : { label: stage.label, icon: stage.icon };
};

const IncidentLifecycle = ({ history, incidentData }) => {
  const rows = Array.isArray(history) ? history : [];

  // Resolve each canonical stage against the incident's history — latest
  // matching entry wins if a stage was logged more than once.
  const resolved = LIFECYCLE_STAGES.map((stage) => {
    const matches = rows
      .filter((h) => stage.test.test(h.remarks || ""))
      .map((h) => ({ ...h, t: parseTime(h.created_on) }))
      .sort((a, b) => b.t - a.t);
    let entry = matches[0] || null;

    // "Incident Created" always has its own timestamp on the incident
    // record, even before/without a dedicated history row for it.
    if (!entry && stage.label === "Incident Created" && incidentData?.created_on) {
      entry = { created_on: incidentData.created_on, remarks: "Incident Created" };
    }

    return { ...stage, entry };
  }).filter((stage) => !stage.optional || stage.entry);

  const matchedCount = resolved.filter((s) => s.entry).length;
  // The furthest-along matched stage is "current"; earlier matched stages
  // are "done"; unmatched stages stay dimmed as "upcoming".
  let lastMatchedIdx = -1;
  resolved.forEach((s, idx) => {
    if (s.entry) lastMatchedIdx = idx;
  });

  return (
    <SectionCard
      icon="⚡"
      title="Incident Lifecycle"
      right={<span className="incv-pill">{matchedCount} of {resolved.length} stages complete</span>}
    >
      <div className="incv-track">
        {resolved.map((stage, idx) => {
          const hasEntry = !!stage.entry;
          const isException = hasEntry && isExceptionRemark(stage.entry.remarks);
          const display = resolveStageDisplay(stage, stage.entry);

          let state = "upcoming";
          if (hasEntry) {
            state = isException ? "exception" : idx === lastMatchedIdx ? "current" : "done";
          }

          return (
            <div className={`incv-node incv-node--${state}`} key={stage.label}>
              {idx > 0 && <div className="incv-line" />}
              <div className="incv-circle" title={stage.entry?.remarks || display.label}>
                <span className="incv-circle-icon">{display.icon}</span>
                {hasEntry && <span className="incv-badge">{isException ? "!" : "✓"}</span>}
              </div>
              <div className="incv-node-label">{display.label}</div>
              <div className="incv-node-time">
                {hasEntry ? (
                  <Moment format="DD-MM-YYYY hh:mm A">{stage.entry.created_on}</Moment>
                ) : (
                  "—"
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="incv-legend">
        <span className="incv-legend-item"><i className="incv-dot incv-dot--done" /> Completed</span>
        <span className="incv-legend-item"><i className="incv-dot incv-dot--current" /> Current</span>
        <span className="incv-legend-item"><i className="incv-dot incv-dot--exception" /> Exception</span>
        <span className="incv-legend-item"><i className="incv-dot incv-dot--upcoming" /> Upcoming</span>
      </div>
    </SectionCard>
  );
};

// ── Closed By (shown only once the incident has actually reached the
// "Incident Closed" stage) — surfaces who closed it from the most recent
// status_id 954 entry in incidentHistoryStatus, since that's the only place
// the closing user's identity is recorded.
const CLOSED_STATUS_ID = 954;

const ClosedByCard = ({ history }) => {
  if (!Array.isArray(history)) return null;

  const closeEntries = history.filter((h) => h.status_id === CLOSED_STATUS_ID);
  if (closeEntries.length === 0) return null;

  const closedBy = closeEntries.reduce((acc, h) => {
    const t = parseTime(h.created_on);
    return !acc || t > acc.t ? { ...h, t } : acc;
  }, null);

  const fullName = [closedBy.first_name, closedBy.last_name].filter(Boolean).join(" ") || "NA";

  return (
    <SectionCard icon="🔒" title="Incident Closed By">
      <div className="incv-grid-2">
        <Field label="Closed By">{fullName}</Field>
        <Field label="Email">{closedBy.created_by || "NA"}</Field>
        <Field label="Mobile Number">{closedBy.mobile_number || "NA"}</Field>
        <Field label="Closed On">
          {closedBy.created_on ? (
            <Moment format="DD-MM-YYYY, hh:mm A">{closedBy.created_on}</Moment>
          ) : (
            "NA"
          )}
        </Field>
      </div>
      {closedBy.remarks && (
        <div className="incv-field mt-3">
          <div className="incv-field-label">Remarks</div>
          <div className="incv-field-value">{closedBy.remarks}</div>
        </div>
      )}
    </SectionCard>
  );
};

// ── Incident Activity History (timeline) ──────────────────────────────────
const IncidentActivityHistory = ({ history }) => {
  if (!Array.isArray(history) || history.length === 0) return null;

  const sorted = [...history].sort((a, b) => parseTime(b.created_on) - parseTime(a.created_on));

  // Multiple log entries can share the same status_id (e.g. several
  // "Resources Deployed" rows for different resources). Group them into a
  // single row, listing every detail together, positioned at the group's
  // most recent occurrence (sorted is already newest-first).
  const groups = [];
  const groupIndexByStatus = new Map();
  sorted.forEach((h) => {
    if (groupIndexByStatus.has(h.status_id)) {
      groups[groupIndexByStatus.get(h.status_id)].entries.push(h);
    } else {
      groupIndexByStatus.set(h.status_id, groups.length);
      groups.push({ status_id: h.status_id, entries: [h] });
    }
  });

  return (
    <SectionCard
      icon="🕘"
      title="Incident Activity Timeline"
      right={<span className="incv-pill">{groups.length} events</span>}
      flush
    >
      <div className="incv-activity-head">
        <span>Activity Name</span>
        <span>Activity Details</span>
        <span>Activity Date &amp; Time</span>
        <span>Activity Done by</span>
      </div>
      <div className="incv-activity-list">
        {groups.map((group, idx) => {
          const latestEntry = group.entries[0];
          const meta = activityMeta(latestEntry.remarks);
          const { title } = splitRemarks(latestEntry.remarks);
          const isLatest = idx === 0;
          const isLast = idx === groups.length - 1;

          // Only collapse true repeat-spam — the same actor logging the
          // exact same detail more than once. Two different actors on the
          // same detail (e.g. both officers on "Flood Situation
          // Assessment") stay as their own line, each with its own
          // timestamp, so Details/Created On/Created By stay aligned
          // line-for-line and no one's contribution is hidden.
          const seen = new Set();
          const lines = group.entries
            .map((entry) => ({
              id: entry.id,
              text: splitRemarks(entry.remarks).description || entry.remarks || "—",
              createdOn: entry.created_on,
              createdBy: entry.created_by || "NA",
            }))
            .filter((line) => {
              const key = `${line.text}||${line.createdBy}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });

          return (
            <div className={`incv-activity-row${isLatest ? " incv-activity-row--latest" : ""}`} key={group.status_id}>
              {!isLast && (
                <>
                  <span className="incv-activity-track" />
                  <span className="incv-activity-arrow" />
                </>
              )}
              <div className="incv-activity-linegrid">
                <div className="incv-activity-event" style={{ gridRow: `span ${lines.length}` }}>
                  <span className="incv-activity-icon" style={{ background: meta.bg, color: meta.color }}>
                    {meta.icon}
                  </span>
                  <span className="incv-activity-title" title={title}>{title}</span>
                  {isLatest && <span className="incv-activity-latest-tag">Latest</span>}
                </div>
                {lines.map((line) => (
                  <React.Fragment key={line.id}>
                    <div className="incv-activity-desc">{line.text}</div>
                    <div className="incv-activity-time">
                      {line.createdOn ? (
                        <Moment format="DD-MM-YYYY, hh:mm A">{line.createdOn}</Moment>
                      ) : (
                        "NA"
                      )}
                    </div>
                    <div className="incv-activity-by">👤 {line.createdBy || "NA"}</div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

// ── Main ────────────────────────────────────────────────────────────────
const IncidentInfo = ({ incidentData }) => {
  if (!incidentData) {
    return <div className="incv-page incv-empty">No incident data available.</div>;
  }

  const latLng = getLatLng(incidentData.gis_config);
  const history = incidentData.incidentHistoryStatus;
  const isClosed =
    incidentData.current_stage === "CLOSED" || incidentData.current_status === "INCIDENT_CLOSED";

  // Origin of this incident (the disaster alert / notification that raised it).
  const source =
    incidentData.sourceInfo && typeof incidentData.sourceInfo === "object" && Object.keys(incidentData.sourceInfo).length > 0
      ? incidentData.sourceInfo
      : null;
  const isSachetSource =
    source && (source.source_type === "SACHET_CAP" || source.source_type === "SACHET_EARTHQUAKE");

  return (
    <div className="incv-page">
      {/* ── Incident Lifecycle (full-width, horizontal) ─ */}
      <IncidentLifecycle history={history} incidentData={incidentData} />

      {/* ── Incident Closed By (only once the incident is actually closed) ── */}
      {isClosed && <ClosedByCard history={history} />}

      {/* ── Incident Details (left) & Incident Location Map (right) ── */}
      <div className="incv-top-row">
        <SectionCard icon="📄" title="Incident Details">
          <div className="incv-grid-2">
            <Field label="Incident ID">{incidentData.incident_seq_id || "NA"}</Field>
            <Field label="Incident Name">{incidentData.name || "NA"}</Field>
            <Field label="Incident Category">{incidentData.category || "NA"}</Field>
            <Field label="Severity">{incidentData.severity || "NA"}</Field>
            <Field label="Incident Created By">{incidentData.created_by || "NA"}</Field>
            <Field label="Incident Date & Time">
              {incidentData.created_on ? (
                <Moment format="DD-MM-YYYY, hh:mm A">{incidentData.created_on}</Moment>
              ) : (
                "NA"
              )}
            </Field>
            {isSachetSource && source.ndma_seq_id && (
              <Field label="NDMA ID">{source.ndma_seq_id}</Field>
            )}
            {source && source.disaster_notification_seq_id && (
              <Field label="Disaster Alert ID">{source.disaster_notification_seq_id}</Field>
            )}
          </div>
          <div className="incv-field mt-3">
            <div className="incv-field-label">Incident Description</div>
            <div className="incv-field-value" style={{ whiteSpace: "pre-line" }}>
              {incidentData.description || "NA"}
            </div>
          </div>
        </SectionCard>

        <IncidentMap latLng={latLng} incidentData={incidentData} />
      </div>

      {/* ── Incident Activity Timeline (left) & Quick Links (right) ── */}
      <div className="incv-links-row">
        <IncidentActivityHistory history={history} />
        <QuickLinks history={history} incidentData={incidentData} />
      </div>
    </div>
  );
};

export default IncidentInfo;
